import { supabase } from '../lib/supabaseClient';
import { smartCacheManager } from './smartCacheManager';

interface RecoveryAction {
  id: string;
  title: string;
  description: string;
  action: () => Promise<boolean>;
  severity: 'low' | 'medium' | 'high';
}

interface SuperAdminNotification {
  id: string;
  title: string;
  message: string;
  errorDetails: any;
  suggestedSolutions: string[];
  timestamp: number;
  severity: 'info' | 'warning' | 'error' | 'critical';
  isRead: boolean;
}

class RecoveryService {
  private notifications: SuperAdminNotification[] = [];

  async attemptAutoRecovery(error: any): Promise<boolean> {
    // Tekrarlayan navigation health check hatalarını bastır
    if (error?.message?.includes('Navigation health check failed')) {
      return false; // Sessiz başarısız olma
    }

    try {
      // 1. Cache clearing
      if (await this.clearCacheRecovery()) {
        return true;
      }

      // 2. Connection retry
      if (await this.connectionRetryRecovery()) {
        return true;
      }

      // 3. Memory cleanup
      if (await this.memoryCleanupRecovery()) {
        return true;
      }

      return false;
    } catch (recoveryError) {
      console.error('Auto recovery failed:', recoveryError);
      return false;
    }
  }

  getUserRecoveryActions(error: any): RecoveryAction[] {
    const actions: RecoveryAction[] = [];

    // Sayfa yenileme
    actions.push({
      id: 'refresh',
      title: 'Sayfayı Yenile',
      description: 'Sayfayı yenileyerek sorunu çözmeyi dene',
      action: async () => {
        window.location.reload();
        return true;
      },
      severity: 'low'
    });

    // Cache temizleme
    actions.push({
      id: 'clear-cache',
      title: 'Önbelleği Temizle',
      description: 'Tarayıcı önbelleğini temizle ve tekrar dene',
      action: async () => {
        smartCacheManager.clearAll();
        localStorage.clear();
        sessionStorage.clear();
        return true;
      },
      severity: 'medium'
    });

    // Oturumu yenileme
    actions.push({
      id: 'refresh-session',
      title: 'Oturumu Yenile',
      description: 'Çıkış yapıp tekrar giriş yap',
      action: async () => {
        await supabase.auth.signOut();
        window.location.href = '/login';
        return true;
      },
      severity: 'high'
    });

    return actions;
  }

  async notifySuperAdmin(error: any, context: any): Promise<void> {
    const notification: SuperAdminNotification = {
      id: `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      title: this.getErrorTitle(error),
      message: this.getErrorMessage(error, context),
      errorDetails: {
        error: error.message || error,
        stack: error.stack,
        context,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href
      },
      suggestedSolutions: this.getSuggestedSolutions(error),
      timestamp: Date.now(),
      severity: this.getErrorSeverity(error),
      isRead: false
    };

    this.notifications.push(notification);

    // Supabase'e kaydet
    try {
      await supabase
        .from('super_admin_notifications')
        .insert({
          notification_id: notification.id,
          title: notification.title,
          message: notification.message,
          error_details: notification.errorDetails,
          suggested_solutions: notification.suggestedSolutions,
          severity: notification.severity,
          is_read: false,
          created_at: new Date().toISOString()
        });
    } catch (dbError) {
      console.error('Failed to save notification to database:', dbError);
    }
  }

  async getSuperAdminNotifications(): Promise<SuperAdminNotification[]> {
    try {
      console.log('Fetching notifications from super_admin_notifications table...');
      
      const { data, error } = await supabase
        .from('super_admin_notifications')
        .select('*')
        .order('created_at', { ascending: false });

      console.log('Query result:', { data, error, count: data?.length });

      if (error) {
        console.error('Database error:', error);
        throw error;
      }

      const notifications = (data || []).map(item => ({
        id: item.notification_id,
        title: item.title,
        message: item.message,
        errorDetails: item.error_details,
        suggestedSolutions: item.suggested_solutions || [],
        timestamp: new Date(item.created_at).getTime(),
        severity: item.severity,
        isRead: item.is_read
      }));

      console.log('Processed notifications:', notifications);
      return notifications;
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      return this.notifications;
    }
  }

  async markNotificationAsRead(notificationId: string): Promise<void> {
    try {
      await supabase
        .from('super_admin_notifications')
        .update({ is_read: true })
        .eq('notification_id', notificationId);

      // Local update
      const notification = this.notifications.find(n => n.id === notificationId);
      if (notification) {
        notification.isRead = true;
      }
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  }

  async deleteNotifications(notificationIds: string[]): Promise<void> {
    try {
      const { error } = await supabase
        .from('super_admin_notifications')
        .delete()
        .in('notification_id', notificationIds);

      if (error) throw error;

      // Local update
      this.notifications = this.notifications.filter(n => !notificationIds.includes(n.id));
    } catch (error) {
      console.error('Failed to delete notifications:', error);
      throw error;
    }
  }

  private async clearCacheRecovery(): Promise<boolean> {
    try {
      smartCacheManager.clearAll();
      return true;
    } catch (error) {
      return false;
    }
  }

  private async connectionRetryRecovery(): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('companies')
        .select('id')
        .limit(1);
      
      return !error;
    } catch (error) {
      return false;
    }
  }

  private async memoryCleanupRecovery(): Promise<boolean> {
    try {
      // Force garbage collection if available
      if ('gc' in window) {
        (window as any).gc();
      }
      
      // Clear unnecessary data
      smartCacheManager.invalidateStaleData();
      
      return true;
    } catch (error) {
      return false;
    }
  }

  private getErrorTitle(error: any): string {
    if (error.message?.includes('network')) return 'Ağ Bağlantı Sorunu';
    if (error.message?.includes('auth')) return 'Kimlik Doğrulama Hatası';
    if (error.message?.includes('database')) return 'Veritabanı Hatası';
    return 'Sistem Hatası';
  }

  private getErrorMessage(error: any, context: any): string {
    return `Hata: ${error.message || error}\nKonum: ${context?.location || 'Bilinmiyor'}\nKullanıcı: ${context?.user?.email || 'Anonim'}`;
  }

  private getSuggestedSolutions(error: any): string[] {
    const solutions: string[] = [];

    if (error.message?.includes('network')) {
      solutions.push('İnternet bağlantısını kontrol edin');
      solutions.push('VPN bağlantısını kontrol edin');
      solutions.push('Firewall ayarlarını kontrol edin');
    }

    if (error.message?.includes('auth')) {
      solutions.push('Kullanıcı oturumunu yenileyin');
      solutions.push('Token sürelerini kontrol edin');
      solutions.push('Kullanıcı yetkilerini kontrol edin');
    }

    if (error.message?.includes('database')) {
      solutions.push('Supabase bağlantısını kontrol edin');
      solutions.push('Database migration durumunu kontrol edin');
      solutions.push('RLS politikalarını kontrol edin');
    }

    solutions.push('Sistem loglarını inceleyin');
    solutions.push('Kullanıcıya alternatif çözüm sunun');

    return solutions;
  }

  private getErrorSeverity(error: any): 'info' | 'warning' | 'error' | 'critical' {
    if (error.message?.includes('critical') || error.message?.includes('fatal')) {
      return 'critical';
    }
    if (error.message?.includes('auth') || error.message?.includes('database')) {
      return 'error';
    }
    if (error.message?.includes('network') || error.message?.includes('timeout')) {
      return 'warning';
    }
    return 'info';
  }
}

export const recoveryService = new RecoveryService();
export type { RecoveryAction, SuperAdminNotification };
